/**
 * RadarTreePage Component
 *
 * Hierarchical tree view of the organization starting from an employee.
 * Shows org chart with performance indicators at each level.
 */
import React from 'react'
import { useParams, useSearch, useNavigate, Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChevronRight,
  ChevronDown,
  Users,
  Minus,
  Plus,
  ArrowLeft,
  Maximize2,
} from 'lucide-react'
import { useRadarTree } from '@/hooks/queries/useRadarQueries'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import { PerformanceScoreInline } from './PerformanceScoreBadge'
import { EngagementIndicator } from './EngagementIndicator'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { RadarTreeNode } from '@/types/radar'

export function RadarTreePage() {
  const { employeeId } = useParams({ from: '/_auth/performance/radar/$employeeId/tree/' })
  const search = useSearch({ from: '/_auth/performance/radar/$employeeId/tree/' })
  const navigate = useNavigate()

  const { getCurrentPeriod } = useRadarUIStore()
  const period = getCurrentPeriod()
  const depth = search.depth || 3

  const { data, isLoading, isError, error } = useRadarTree(employeeId, depth, period)

  // Expanded nodes state
  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(new Set())

  // Auto-expand first two levels on load
  React.useEffect(() => {
    if (data?.root) {
      const initialExpanded = new Set<string>([data.root.employee.employeeId])
      data.root.children.forEach((child) => {
        initialExpanded.add(child.employee.employeeId)
      })
      setExpandedNodes(initialExpanded)
    }
  }, [data])

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return next
    })
  }

  const expandAll = () => {
    if (!data) return
    const allIds = new Set<string>()
    const traverse = (node: RadarTreeNode) => {
      allIds.add(node.employee.employeeId)
      node.children.forEach(traverse)
    }
    traverse(data.root)
    setExpandedNodes(allIds)
  }

  const collapseAll = () => {
    if (!data) return
    setExpandedNodes(new Set([data.root.employee.employeeId]))
  }

  const handleDepthChange = (newDepth: number) => {
    navigate({
      to: '/performance/radar/$employeeId/tree',
      params: { employeeId },
      search: { depth: newDepth },
    })
  }

  if (isLoading) {
    return <TreeSkeleton />
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Erro ao carregar árvore</h2>
        <p className="text-brand-gray mb-4">
          {error instanceof Error ? error.message : 'Não foi possível carregar a hierarquia.'}
        </p>
        <Link
          to="/performance/radar/$employeeId"
          params={{ employeeId }}
          className="text-brand-primary hover:underline"
        >
          Voltar ao radar
        </Link>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Validate required data structure
  if (!data.root || !data.metadata) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Dados incompletos</h2>
        <p className="text-brand-gray mb-4">
          A API retornou dados incompletos para a árvore organizacional.
        </p>
        <Link
          to="/performance/radar/$employeeId"
          params={{ employeeId }}
          className="text-brand-primary hover:underline"
        >
          Voltar ao radar
        </Link>
      </div>
    )
  }

  const { root, totalEmployees, hierarchyDepth, metadata } = data
  const lastUpdated = formatDistanceToNow(new Date(metadata.generatedAt), {
    addSuffix: true,
    locale: ptBR,
  })

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-brand-gray">
        <Link to="/performance/radar" className="hover:text-white">
          Radar
        </Link>
        <ChevronRight className="w-4 h-4" />
        <Link
          to="/performance/radar/$employeeId"
          params={{ employeeId }}
          className="hover:text-white"
        >
          {root.employee.name}
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">Árvore</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Árvore Organizacional</h2>
          <p className="text-brand-gray">
            {totalEmployees} funcionários • {hierarchyDepth} níveis de profundidade
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Depth control */}
          <div className="flex items-center gap-1 bg-brand-dark-light border border-brand-gray/30 rounded-lg">
            <button
              onClick={() => handleDepthChange(Math.max(1, depth - 1))}
              disabled={depth <= 1}
              className="p-2 text-brand-gray hover:text-white disabled:opacity-50"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="px-2 text-white text-sm">Profundidade: {depth}</span>
            <button
              onClick={() => handleDepthChange(Math.min(15, depth + 1))}
              disabled={depth >= 15}
              className="p-2 text-brand-gray hover:text-white disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Expand/Collapse */}
          <button
            onClick={expandAll}
            className="px-3 py-2 text-sm border border-brand-gray/30 rounded-lg text-brand-gray hover:text-white hover:border-brand-gray/50"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-2 text-sm border border-brand-gray/30 rounded-lg text-brand-gray hover:text-white hover:border-brand-gray/50"
          >
            Recolher
          </button>

          {/* Back */}
          <Link
            to="/performance/radar/$employeeId"
            params={{ employeeId }}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-brand-gray/30 rounded-lg text-brand-gray hover:text-white hover:border-brand-gray/50"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Radar
          </Link>
        </div>
      </div>

      {/* Tree */}
      <Card>
        <CardContent className="p-4 overflow-x-auto">
          <TreeNode
            node={root}
            depth={0}
            isExpanded={expandedNodes.has(root.employee.employeeId)}
            onToggle={toggleNode}
            expandedNodes={expandedNodes}
            onNavigate={(id) => navigate({
              to: '/performance/radar/$employeeId',
              params: { employeeId: id }
            })}
          />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-brand-gray">
        Última atualização: {lastUpdated}
      </div>
    </div>
  )
}

/**
 * Tree node component (recursive)
 */
function TreeNode({
  node,
  depth,
  isExpanded,
  onToggle,
  expandedNodes,
  onNavigate,
}: {
  node: RadarTreeNode
  depth: number
  isExpanded: boolean
  onToggle: (id: string) => void
  expandedNodes: Set<string>
  onNavigate: (id: string) => void
}) {
  const hasChildren = node.children.length > 0
  const indentPx = depth * 24

  return (
    <div>
      {/* Node row */}
      <div
        className={cn(
          'flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-brand-dark-light/50 transition-colors',
          depth === 0 && 'bg-brand-dark-light'
        )}
        style={{ paddingLeft: indentPx + 12 }}
      >
        {/* Expand/Collapse button */}
        <button
          onClick={() => onToggle(node.employee.employeeId)}
          disabled={!hasChildren}
          className={cn(
            'w-6 h-6 flex items-center justify-center rounded',
            hasChildren ? 'text-brand-gray hover:text-white hover:bg-brand-dark-light' : 'text-transparent'
          )}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          ) : (
            <span className="w-4 h-4" />
          )}
        </button>

        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-brand-gray/30 flex items-center justify-center flex-shrink-0">
          {node.employee.pictureUrl ? (
            <img
              src={node.employee.pictureUrl}
              alt={node.employee.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <span className="text-sm font-medium text-white">
              {node.employee.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Name and role */}
        <button
          onClick={() => onNavigate(node.employee.employeeId)}
          className="text-left min-w-0 hover:text-brand-primary transition-colors"
        >
          <div className="text-white font-medium truncate">{node.employee.name}</div>
          <div className="text-brand-gray text-xs truncate">{node.employee.role}</div>
        </button>

        {/* Metrics */}
        <div className="flex items-center gap-4 ml-auto flex-shrink-0">
          <PerformanceScoreInline score={node.performanceScore} />
          {node.teamSize > 0 && (
            <span className="text-brand-gray text-sm flex items-center gap-1">
              <Users className="w-4 h-4" />
              {node.teamSize}
            </span>
          )}
          <EngagementIndicator signal={node.engagementSignal} size="sm" />
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="border-l border-brand-gray/20 ml-6">
          {node.children.map((child) => (
            <TreeNode
              key={child.employee.employeeId}
              node={child}
              depth={depth + 1}
              isExpanded={expandedNodes.has(child.employee.employeeId)}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Loading skeleton
 */
function TreeSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-5 w-64 bg-brand-gray/20" />
      <div className="flex justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48 bg-brand-gray/20" />
          <Skeleton className="h-5 w-32 bg-brand-gray/20" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32 bg-brand-gray/20 rounded-lg" />
          <Skeleton className="h-10 w-24 bg-brand-gray/20 rounded-lg" />
        </div>
      </div>
      <Card>
        <CardContent className="p-4">
          <div className="space-y-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ paddingLeft: Math.min(i, 3) * 24 }}>
                <Skeleton className="h-12 w-full bg-brand-gray/20 rounded-lg" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
